import React from 'react';
import {
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TextField,
    Select,
    Box,
    MenuItem,
    Paper,
    Typography
} from '@mui/material';

export interface MicrobeData {
    bacteria_name: string;
    gut_bacteria_percent: number;
    bacteria_prevalence_score: number;
    section_name: string;
}

type SectionName = 
    | 'most_abundant_bacteria'
    | 'favourable_bacteria'
    | 'pathogenic_bacteria'
    | 'opportunistic_pathogens'
    | 'probiotic_bacteria';

interface MicrobeCompositionEditorProps {
    data: MicrobeData[];
    onChange: (index: number, field: keyof MicrobeData, value: any) => void;
}

const sections: SectionName[] = [
    'most_abundant_bacteria',
    'favourable_bacteria',
    'pathogenic_bacteria',
    'opportunistic_pathogens',
    'probiotic_bacteria'
];

const formatSectionName = (name: string) => {
    return name
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const MicrobeCompositionEditor: React.FC<MicrobeCompositionEditorProps> = ({ data, onChange }) => {
    // Ensure data is an array before mapping
    const displayData = Array.isArray(data) ? data : [];

    return (
        <TableContainer component={Paper} sx={{ maxHeight: '70vh', overflow: 'auto' }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>Bacteria Name</TableCell>
                        <TableCell>Percentage</TableCell>
                        <TableCell>Prevalence Score</TableCell>
                        <TableCell>Section</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {displayData.map((item, index) => {
                        // Ensure item is an object with the required properties
                        const bacteriaName = item?.bacteria_name || '';
                        const percentage = typeof item?.gut_bacteria_percent === 'number' ? item.gut_bacteria_percent : 0;
                        const score = typeof item?.bacteria_prevalence_score === 'number' ? item.bacteria_prevalence_score : 0;
                        const section = item?.section_name || sections[0] || '';

                        return (
                            <TableRow key={index}>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        value={bacteriaName}
                                        onChange={(e) => onChange(index, 'bacteria_name', e.target.value)}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        type="number"
                                        size="small"
                                        fullWidth
                                        value={percentage}
                                        onChange={(e) => onChange(index, 'gut_bacteria_percent', parseFloat(e.target.value) || 0)}
                                        inputProps={{ 
                                            step: '0.01',
                                            min: '0',
                                            max: '100'
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        type="number"
                                        size="small"
                                        fullWidth
                                        value={score}
                                        onChange={(e) => onChange(index, 'bacteria_prevalence_score', parseFloat(e.target.value) || 0)}
                                        inputProps={{ 
                                            step: '0.01',
                                            min: '0',
                                            max: '10'
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Select
                                        size="small"
                                        fullWidth
                                        value={section}
                                        onChange={(e) => onChange(index, 'section_name', e.target.value as SectionName)}
                                    >
                                        {sections.map((s) => (
                                            <MenuItem key={s} value={s}>
                                                {formatSectionName(s)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
            {displayData.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="textSecondary">No microbe data available</Typography>
                </Box>
            )}
        </TableContainer>
    );
};

export default MicrobeCompositionEditor;
